import axios, { AxiosResponse } from 'axios';
import requestHandler from './requestHandler';
import { IS_PROD, IS_STAGING, HOSTNAME_IS_TEST } from '../constants/environment';
import { RequestResponse } from './requestHandler';
export { default as requestHandler } from './requestHandler';

export const noop = (): void => {
    // Silence lint
};

interface Post {
    comments: number;
    likes: number;
    shares: number;
    network: string;
    impressions: number;
}

interface queryType {
    query: string;
    variables: {
        ids: string[];
    };
}

interface UploadImagePayload {
    type: string;
    image: File;
    brandName?: string;
}

const states: Record<string, string> = {
    Arizona: 'AZ',
    Alabama: 'AL',
    Alaska: 'AK',
    Arkansas: 'AR',
    California: 'CA',
    Colorado: 'CO',
    Connecticut: 'CT',
    Delaware: 'DE',
    Florida: 'FL',
    Georgia: 'GA',
    Hawaii: 'HI',
    Idaho: 'ID',
    Illinois: 'IL',
    Indiana: 'IN',
    Iowa: 'IA',
    Kansas: 'KS',
    Kentucky: 'KY',
    Louisiana: 'LA',
    Maine: 'ME',
    Maryland: 'MD',
    Massachusetts: 'MA',
    Michigan: 'MI',
    Minnesota: 'MN',
    Mississippi: 'MS',
    Missouri: 'MO',
    Montana: 'MT',
    Nebraska: 'NE',
    Nevada: 'NV',
    'New Hampshire': 'NH',
    'New Jersey': 'NJ',
    'New Mexico': 'NM',
    'New York': 'NY',
    'North Carolina': 'NC',
    'North Dakota': 'ND',
    Ohio: 'OH',
    Oklahoma: 'OK',
    Oregon: 'OR',
    Pennsylvania: 'PA',
    'Rhode Island': 'RI',
    'South Carolina': 'SC',
    'South Dakota': 'SD',
    Tennessee: 'TN',
    Texas: 'TX',
    Utah: 'UT',
    Vermont: 'VT',
    Virginia: 'VA',
    Washington: 'WA',
    'West Virginia': 'WV',
    Wisconsin: 'WI',
    Wyoming: 'WY',
};

// I live because because lighten() and darken() are native only to SCSS.
export const lightenOrDarken = (
    color: string,
    percent: number,
    alpha: number = 1,
    returnAsHex: boolean,
): string => {
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);
    const newR = Math.min(255, Math.max(0, r + percent * 2.55));
    const newG = Math.min(255, Math.max(0, g + percent * 2.55));
    const newB = Math.min(255, Math.max(0, b + percent * 2.55));
    const newColor = returnAsHex
        ? `#${Math.round(newR).toString(16).padStart(2, '0')}${Math.round(newG)
              .toString(16)
              .padStart(2, '0')}${Math.round(newB).toString(16).padStart(2, '0')}`
        : `rgba(${newR}, ${newG}, ${newB}, ${alpha})`;
    return newColor;
};

export const uploadImageToS3 = async (
    payload: UploadImagePayload,
): Promise<string | boolean | null> => {
    const { type, image, brandName } = payload;
    const name = image?.name || 'logo.jpg';
    const fileType = image.type;
    try {
        const res = await requestHandler<{ uploadUrl: string }>(
            'POST',
            '/api/public/getSignedUrl',
            {
                type,
                name,
                fileType,
                image,
                brandName,
            },
        );
        if (res.success) {
            const fetchRes = await fetch(res.payload.uploadUrl, {
                method: 'PUT',
                body: image,
            });
            if (fetchRes.ok) {
                return fetchRes.url.split('?')[0];
            }
        }
        return false;
    } catch (e) {
        console.log({ e });
        return null;
    }
};

function fallbackCopyTextToClipboard(text: string): void {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    //Avoid scrolling to bottom
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.style.position = 'fixed';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
        const successful = document.execCommand('copy');
        const msg = successful ? 'successful' : 'unsuccessful';
        console.log(`Fallback: Copying text command was ${msg}`);
    } catch (err) {
        console.error('Fallback: Unable to copy', err);
    }
    document.body.removeChild(textArea);
}

export const isIOS = (): boolean => {
    return (
        /iPad|iPhone|iPod/.test(navigator.userAgent) &&
        !(window as Window & { MSStream?: boolean }).MSStream
    );
};

export const copyToClipboard = (
    text: string,
    options: { allowThrow?: boolean } = { allowThrow: false },
): Promise<void> | undefined => {
    if (!navigator.clipboard) {
        fallbackCopyTextToClipboard(text);
        return undefined;
    }
    if (options.allowThrow) {
        return navigator.clipboard.writeText(text);
    }
    navigator.clipboard.writeText(text).catch(() => {
        //Handle error silently
    });
    return undefined;
};

export const hexToRgb = (hex: string): string => {
    const hexCode = hex.replace('#', '');
    const arrBuff = new ArrayBuffer(4);
    const vw = new DataView(arrBuff);
    vw.setUint32(0, parseInt(hexCode, 16), false);
    const arrByte = new Uint8Array(arrBuff);
    const rbg = `${arrByte[1]},${arrByte[2]},${arrByte[3]}`;
    return rbg;
};

export const sleep = (ms: number): Promise<void> => {
    return new Promise((resolve) => {
        setTimeout(() => {
            resolve();
        }, ms);
    });
};

export const formatLocation = (location: string): string => {
    const split = location.split(',');
    return split
        .map((word: string, i: number) => {
            if (states[word]) {
                if (word === 'Washington' && split[i + 1] === 'DC') {
                    return word;
                }
                return states[word];
            }
            return word;
        })
        .join(', ')
        .replace(', United States', '')
        .replace('of America', '');
};

export const removeDuplicatesFromArrayByKey = <T, K>(keyFn: (item: T) => K, array: T[]): T[] => {
    const set = new Set<K>();
    return array.filter((x) => {
        const key = keyFn(x);
        const isNew = !set.has(key);
        if (isNew) set.add(key);
        return isNew;
    });
};

export const abbreviateState = (state: string): string => {
    return states[state];
};

export const numWithCommas = (num: number | string, fixed?: number): string => {
    if (num === undefined || num === null) return '0';
    if (!Number.isNaN(parseInt(`${num}`, 10))) {
        if (num.toString().length !== parseInt(`${num}`, 10).toString().length) {
            const int = parseInt(`${num}`, 10);
            let decimal: string | number = Number(num) - int;
            let returnVal = int.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
            if (fixed !== undefined) decimal = decimal.toFixed(fixed);
            returnVal += decimal.toString().substring(1);
            return returnVal;
        }
        if (fixed !== undefined && typeof num === 'string') {
            return Number(num)
                .toFixed(fixed)
                .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        }
        if (fixed !== undefined && typeof num === 'number')
            return num.toFixed(fixed).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }
    return '0';
};

export const cents2Dollars = (num: number | string): string => {
    if (typeof num === 'string') {
        num = Number(num);
    }
    return numWithCommas(num / 100, 2);
};

export const cents2DollarsNoCommas = (num: number): string => (num / 100).toFixed(2);
export const cents2DollarsNoCommasNoDecimals = (num: number): number => num / 100;

export const capitalCase = (str: string | null): string => {
    if (!str) return '';
    if (str === 'youtube') return 'YouTube';
    if (str === 'tiktok') return 'TikTok';
    if (str === 'smb') return 'SMB';
    return `${str.charAt(0).toUpperCase()}${str.substring(1).toLowerCase()}`;
};

export const getUserLocale = (): string => {
    const userLocale = navigator.language;
    return userLocale;
};

export const decimalToPercentage = (decimal: number): string => {
    if (Number.isNaN(decimal)) return '0%';
    const percentage = decimal * 100;
    return `${percentage.toFixed(0)}%`;
};

export const symbolizeNums = (
    num: number | string | null,
    precision: number = 0,
    fraction: boolean = false,
    percent: boolean = false,
    charLimit: number = 1000000000,
    truncateZeroes: boolean = false,
): string => {
    if (Number.isNaN(Number(num)) || num === null || num === '') return '';
    let numValue = typeof num === 'string' ? Number(num) : num;
    if (percent || fraction) {
        numValue = Number(numValue.toFixed(precision));
    } else if (!fraction) {
        numValue = Math.round(numValue);
    }
    const ret: { symbol: string; divisor: number } = {
        symbol: '',
        divisor: 1,
    };
    let neg: boolean = false;
    if (Math.sign(numValue) === -1) {
        numValue = Math.abs(numValue);
        neg = true;
    }
    const numStr = numValue.toString();
    if (
        ((percent || fraction) && numStr.length - precision - 1 > 12) ||
        (!percent && numStr.split('.')[0].length > 12)
    ) {
        ret.symbol = 'T';
        ret.divisor = 1000000000000;
    } else if (
        ((percent || fraction) && numStr.length - precision - 1 > 9) ||
        (!percent && numStr.split('.')[0].length > 9)
    ) {
        ret.symbol = 'B';
        ret.divisor = 1000000000;
    } else if (
        ((percent || fraction) && numStr.length - precision - 1 > 6) ||
        (!percent && numStr.split('.')[0].length > 6)
    ) {
        ret.symbol = 'M';
        ret.divisor = 1000000;
    } else if (
        ((percent || fraction) && numStr.length - precision - 1 > 3) ||
        (!percent && numStr.split('.')[0].length > 3)
    ) {
        ret.symbol = 'K';
        ret.divisor = 1000;
    }
    let reduced: number;
    if (neg) reduced = (numValue * -1) / ret.divisor;
    else reduced = numValue / ret.divisor;
    if (ret.divisor > 1) {
        reduced = Number(reduced.toFixed(precision));
        if (truncateZeroes) reduced = Number(reduced);
        if (percent && reduced.toString().length + 2 > charLimit)
            reduced = Number(reduced.toFixed(0));
        else if (reduced.toString().length + 1 > charLimit) reduced = Number(reduced.toFixed(0));
    }
    if (percent && ret.symbol !== '') return `${reduced}${ret.symbol} %`;
    if (percent) return `${reduced}${ret.symbol}%`;
    return `${reduced}${ret.symbol}`;
};

export const networkColors = (val: string): string => {
    switch (val) {
        case 'youtube':
            return '#ff0000';
        case 'twitter':
            return '#00aced';
        case 'facebook':
            return '#3b5998';
        case 'instagram':
            return 'purple';
        case 'tiktok':
            return '#FF2C55';
        default:
            return '#fff';
    }
};

export const formatCurrency = (
    number: number | string,
    round: boolean = false,
    decimal: boolean = true,
): string => {
    if (typeof number === 'string') {
        number = Number(number);
    }
    if (round) {
        number = Math.round(number);
    }
    const val: string = Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
        number,
    );
    if (decimal) return val;
    return val.split('.')[0];
};

export const formatLargeNum = (num: number): string => {
    if (num === undefined || num === null) return '0';
    if (num >= 1000000000) return `${Math.round(num / 100000000) / 10}B`;
    if (num >= 1000000) return `${Math.round(num / 100000) / 10}M`;
    if (num > 1000) return `${Math.round(num / 1000)}k`;
    return num.toString();
};

export const findAudienceEligibleNetworks = (
    audienceData: Record<string, { hasData: boolean }>,
) => {
    const networks: string[] = [];
    Object.entries(audienceData).forEach(([key, value]) => {
        if (value.hasData) networks.push(key);
    });
    return networks;
};

export const findHighReachNetwork = (
    reach: Record<string, { total: number }>,
    reaches: Record<string, { total: number }>,
    availableNetworks: string[] = ['facebook', 'twitter', 'instagram', 'youtube', 'tiktok'],
): string => {
    if (!reach) reach = reaches;
    let high = 0;
    let network = 'none';
    Object.entries(reach).forEach(([key, value]) => {
        if (availableNetworks.indexOf(key) !== -1 && value && value.total > high) {
            high = value.total;
            network = key;
        }
    });
    return network;
};

export const truncateText = (text: string | undefined, charLength: number): string => {
    if (text && text.length > charLength) return text.slice(0, charLength).trim().concat('...');
    return text || '';
};

export function validEmail(email: string): boolean {
    const re =
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
}

export function engagementRate(post: Post, reach: Record<string, { total: number }>): number {
    // YouTube Engagement Rate: (Likes + Comments + Favorites[if known]) / Views
    // Instagram Engagement Rate: (Likes + Comments) / Followers
    // Facebook Engagement Rate: (Likes + Comments + Shares) / Followers
    // Twitter Engagement Rate: (Likes + Comments + Retweets) / Followers
    const { comments, likes, shares, network, impressions: views } = post;
    // last 10 most recent posts if its an influencer
    // yt is (likes + ) / views(impressions)
    let n = 0;
    switch (network) {
        case 'youtube': {
            const hasLikesOrCommentsAndViews = likes > 0 || (comments > 0 && views > 0);
            n = hasLikesOrCommentsAndViews ? (likes + comments) / views : 0;
            break;
        }
        case 'instagram': {
            const hasLikesOrCommentsAndReach =
                (likes > 0 || comments > 0) && reach?.instagram?.total > 0;
            n = hasLikesOrCommentsAndReach ? (likes + comments) / reach?.instagram?.total : 0;
            break;
        }
        case 'facebook': {
            const hasLikesOrCommentsOrSharesAndReach =
                (likes > 0 || comments > 0 || shares > 0) && reach?.facebook?.total > 0;
            n = hasLikesOrCommentsOrSharesAndReach
                ? (likes + comments + shares) / reach?.facebook?.total
                : 0;
            break;
        }
        case 'twitter': {
            const hasLikesOrCommentsOrSharesAndReach =
                (likes > 0 || comments > 0 || shares > 0) && reach?.twitter?.total > 0;
            n = hasLikesOrCommentsOrSharesAndReach
                ? (likes + comments + shares) / reach?.twitter?.total
                : 0;
            break;
        }
        case 'tiktok': {
            const hasLikesOrCommentsOrSharesAndViews =
                (likes > 0 || comments > 0 || shares > 0) && views > 0;
            n = hasLikesOrCommentsOrSharesAndViews ? (likes + comments + shares) / views : 0;
            break;
        }
        default:
            break;
    }
    return n * 100;
}

export function dtcCampaignEngagementRate(post: Post, reach: number): number {
    // YouTube Engagement Rate: (Likes + Comments + Favorites[if known]) / Views
    // Instagram Engagement Rate: (Likes + Comments) / Followers
    // Facebook Engagement Rate: (Likes + Comments + Shares) / Followers
    // Twitter Engagement Rate: (Likes + Comments + Retweets) / Followers
    const { comments, likes, shares, network, impressions: views } = post;
    // last 10 most recent posts if its an influencer
    // yt is (likes + ) / views(impressions)
    let n: number = 0;
    switch (network) {
        case 'youtube':
            n = (likes + comments) / views;
            break;
        case 'instagram':
            n = (likes + comments) / reach;
            break;
        case 'facebook':
            n = (likes + comments + shares) / reach;
            break;
        case 'twitter':
            n = (likes + comments + shares) / reach;
            break;
        case 'tiktok':
            n = (likes + comments) / reach;
            break;
        default:
            break;
    }
    return n * 100;
}

export const storeFrontRequestHandler = async (
    shopUrl: string,
    accessToken: string,
    query: queryType,
): Promise<AxiosResponse | Error> => {
    const requestObj = {
        method: 'POST',
        url: `https://${shopUrl}/api/${process.env.SHOPIFY_API_VERSION}/graphql.json`,
        headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': accessToken,
        },
        data: JSON.stringify(query),
    };
    try {
        const res = await axios(requestObj);
        return res;
    } catch (error) {
        return error as Error;
    }
};

export const fixSnakeCase = (str: string): string =>
    str
        .split('_')
        .map((word) => `${word.charAt(0).toUpperCase()}${word.substring(1)}`)
        .join(' ');

export const camelCase2CapitalCase = (str: string): string => {
    const val = str.replace(/([A-Z])/g, ' $1');
    const done = str.charAt(0).toUpperCase() + val.slice(1);
    return done;
};

export const multiWordCapitalCase = (str: string): string => {
    if (!str) return '';
    return str
        .split(' ')
        .map((s) => `${s.charAt(0).toUpperCase()}${s.substring(1).toLowerCase()}`)
        .join(' ');
};

type FileResponse = RequestResponse<ArrayBuffer> & {
    headers: {
        'content-type': string;
    };
    data: ArrayBuffer;
};

export const openFileInTab = async (
    path: string,
    fn: (value: unknown) => void,
    value: unknown,
    onError: (error: string) => void,
) => {
    const res = (await requestHandler(
        'GET',
        path,
        null,
        { responseType: 'arraybuffer' },
        undefined,
        true,
    )) as FileResponse; // TODO: fix this type casting.

    if (res.success) {
        const file = new Blob([res.data], {
            type: res.headers['content-type'],
        });
        const fileURL = URL.createObjectURL(file);
        fn(value);
        window.open(fileURL, '_blank');
    } else if (typeof onError === 'function') {
        onError('Error Opening file');
    }
};

export const getScreenNameFromUrl = (url: string): string => {
    const split = url.split('/');
    return split[split.length - 1];
};

export function convertAbbreviatedNumber(abbreviatedNumber: string): string | number {
    const multipliers: { [key: string]: number } = {
        k: 1000,
        m: 1000000,
        b: 1000000000,
        t: 1000000000000,
    };
    abbreviatedNumber = abbreviatedNumber.trim().toLowerCase();

    if (!abbreviatedNumber || isNaN(parseFloat(abbreviatedNumber.slice(0, -1)))) {
        return abbreviatedNumber;
    }

    const suffix = abbreviatedNumber.slice(-1).toLowerCase();
    const numberPart = parseFloat(abbreviatedNumber.slice(0, -1));

    if (multipliers[suffix]) {
        return numberPart * multipliers[suffix];
    }
    return parseFloat(abbreviatedNumber);
}

export function convertConstantToString(value: string): string {
    return value
        .toLowerCase()
        .replace(/_/g, ' ')
        .replace(/(^\w|\s\w)/g, (m) => m.toUpperCase());
}

export function getLabelFromHash(hash: string): string {
    return hash.slice(1).replace(/^(\w)/, (f) => f.toUpperCase()); // for example, #application -> Application, etc.
}

export function loginFromToken(token: string, host: string): void {
    if (IS_PROD || (IS_STAGING && !HOSTNAME_IS_TEST)) {
        window.location.href = `https://${host}?__csst=${token}`;
    } else {
        window.location.href = `http://localhost:3000?__csst=${token}`;
    }
}

export const convertCentsToUSD = (price: number, includeCents: boolean = true) => {
    return (price / 100).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: includeCents ? 2 : 0,
        maximumFractionDigits: includeCents ? 2 : 0,
    });
};

export const getStringEnumKeyByValue = <T>(enumObj: T, value: string): string | undefined => {
    for (const key in enumObj) {
        if (enumObj[key as keyof T] === value) return key;
    }
    return undefined;
};

function stringToColor(string: string | undefined | null) {
    if (!string) return '#40B3F3';
    let hash = 0;
    let i;

    for (i = 0; i < string.length; i += 1) {
        hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }

    let color = '#';

    for (i = 0; i < 3; i += 1) {
        const value = (hash >> (i * 8)) & 0xff;
        color += `00${value.toString(16)}`.slice(-2);
    }

    return color;
}

export function stringAvatar(name: string | undefined | null, size: number = 40) {
    return {
        sx: {
            bgcolor: stringToColor(name),
            width: size,
            height: size,
            fontSize: `${size / 2.25}px !important`,
            fontWeight: 'bold !important',
        },
        children: (() => {
            const trimmed = name?.trim();
            if (!trimmed) return 'N/A';
            const parts = trimmed.split(/\s+/).filter(Boolean);
            if (parts.length > 1) {
                return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
            }
            return parts[0].slice(0, 2).toUpperCase();
        })(),
    };
}

export const checkIfFilter = (data: { min: number | string; max: number | string }) => {
    if (
        !data ||
        (data.min === '' && data.max === '') ||
        (data.min === '0' && data.max === '0') ||
        (data.min === 0 && data.max === 0)
    ) {
        return false;
    }
    return true;
};

export const extractErrorMessages = (error: unknown, visited = new WeakSet()): string[] => {
    // Prevent infinite recursion for circular references
    if (typeof error === 'object' && error !== null && visited.has(error)) {
        return [];
    }

    if (typeof error === 'object' && error !== null) {
        visited.add(error);
    }

    if (typeof error === 'string' && error.trim()) {
        return [error.trim()];
    }

    if (
        typeof error === 'object' &&
        error !== null &&
        'message' in error &&
        typeof error.message === 'string' &&
        error.message.trim()
    ) {
        return [error.message.trim()];
    }

    if (Array.isArray(error)) {
        const messages: string[] = [];
        for (const item of error) {
            messages.push(...extractErrorMessages(item, visited));
        }
        return messages;
    }

    if (typeof error === 'object' && error !== null) {
        const messages: string[] = [];
        for (const value of Object.values(error)) {
            messages.push(...extractErrorMessages(value, visited));
        }
        return messages;
    }

    return [];
};
